/**
 * Tenant store layout — sidebar tenant-style (parecido a Skeleton actual).
 * Live FUERA de _app.tsx para tener su propia chrome (no el sidebar de plataforma).
 *
 * Auth: si activeTenantApiKey aún no está seteada para este tenant, la auto-genera
 * vía /api/admin/api-keys/* (necesario para llamar /api/v1/* desde el panel).
 */
import { createFileRoute, redirect, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, Package, FolderTree, Layers, ShoppingCart, Users, Tag, BookOpen,
  Settings, Truck, CreditCard, BarChart3, Mail, ArrowLeft, Shield,
  Warehouse, Star, MessageCircle, RotateCcw, Award, Sparkles, ChevronDown, LogOut, Check, Mail as MailIcon,
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/cn'
import { useAuth } from '@/lib/auth-store'
import { adminApi } from '@/lib/api'
import { Badge } from '@/components/ui'

export const Route = createFileRoute('/store/$id')({
  beforeLoad: () => {
    if (!useAuth.getState().isAuthenticated()) throw redirect({ to: '/login' })
  },
  component: StoreLayout,
})

interface Tenant {
  id: string
  slug: string
  name: string
  status: string
  defaultCurrency: string
  defaultLocale: string
  primaryHostname: string | null
  metadata: Record<string, string>
}

interface ApiKey {
  id: string
  prefix: string
  environment: 'live' | 'test'
}

// Nav siguiendo el orden de Skeleton actual (1:1) — agrupado en secciones lógicas.
const STORE_NAV: Array<
  | { type: 'item'; to: string; label: string; icon: React.ComponentType<{ className?: string }>; exact?: boolean }
  | { type: 'section'; label: string }
> = [
  { type: 'item', to: '/store/$id', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { type: 'section', label: 'Catálogo' },
  { type: 'item', to: '/store/$id/products', label: 'Productos', icon: Package },
  { type: 'item', to: '/store/$id/categories', label: 'Categorías', icon: FolderTree },
  { type: 'item', to: '/store/$id/product-types', label: 'Tipos', icon: Layers },
  { type: 'item', to: '/store/$id/inventory', label: 'Inventario', icon: Warehouse },
  { type: 'section', label: 'Ventas' },
  { type: 'item', to: '/store/$id/orders', label: 'Pedidos', icon: ShoppingCart },
  { type: 'item', to: '/store/$id/customers', label: 'Clientes', icon: Users },
  { type: 'item', to: '/store/$id/returns', label: 'Devoluciones', icon: RotateCcw },
  { type: 'item', to: '/store/$id/coupons', label: 'Cupones', icon: Tag },
  { type: 'item', to: '/store/$id/loyalty', label: 'Fidelización', icon: Award },
  { type: 'item', to: '/store/$id/shipping', label: 'Envíos', icon: Truck },
  { type: 'item', to: '/store/$id/payments', label: 'Pagos', icon: CreditCard },
  { type: 'section', label: 'Contenido' },
  { type: 'item', to: '/store/$id/blog', label: 'Blog', icon: BookOpen },
  { type: 'item', to: '/store/$id/comments', label: 'Comentarios', icon: MessageCircle },
  { type: 'item', to: '/store/$id/reviews', label: 'Reviews', icon: Star },
  { type: 'section', label: 'Crecimiento' },
  { type: 'item', to: '/store/$id/marketing', label: 'Marketing', icon: Mail },
  { type: 'item', to: '/store/$id/analytics', label: 'Analítica', icon: BarChart3 },
  { type: 'item', to: '/store/$id/reports', label: 'Reportes', icon: BarChart3 },
  { type: 'section', label: 'Sistema' },
  { type: 'item', to: '/store/$id/email-templates', label: 'Plantillas email', icon: MailIcon },
  { type: 'item', to: '/store/$id/audit', label: 'Auditoría', icon: Shield },
  { type: 'item', to: '/store/$id/settings', label: 'Ajustes', icon: Settings },
]

function StoreLayout() {
  const { id } = Route.useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { activeTenantId, activeTenantApiKey, setActiveTenant } = useAuth()

  // Cargar info del tenant
  const { data: tenantData } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => adminApi.get<{ data: Tenant }>(`/api/admin/tenants/${id}`),
  })

  // Cargar API keys del tenant para auto-seleccionar la primera live
  const { data: keysData, isFetched: keysFetched } = useQuery({
    queryKey: ['tenant-api-keys', id],
    queryFn: () => adminApi.get<{ data: ApiKey[] }>(`/api/admin/api-keys/tenant/${id}`),
  })

  // Si no hay API key activa para este tenant, intentar usar la primera live
  useEffect(() => {
    if (activeTenantId === id && activeTenantApiKey) return // ya hay key activa
    if (!keysFetched || !keysData) return

    const liveKey = keysData.data.find((k) => k.environment === 'live')
    // Nota: el backend NO devuelve el fullKey en list (solo prefix), así que no podemos
    // setear la key sin generar una nueva. El usuario debe generarla manualmente desde
    // /tenants/$id (admin view) y guardarla. Por ahora seteamos solo el tenantId.
    setActiveTenant(id, activeTenantApiKey ?? null)
    void liveKey // referenced to avoid unused warning
  }, [keysFetched, keysData, id, activeTenantId, activeTenantApiKey, setActiveTenant])

  const tenant = tenantData?.data

  return (
    <div className="flex h-screen bg-neutral-950 text-neutral-100">
      <aside className="w-64 border-r border-neutral-800 bg-neutral-900/50 flex flex-col">
        <SidebarHeader currentTenantId={id} tenant={tenant} />

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {STORE_NAV.map((entry, idx) => {
            if (entry.type === 'section') {
              return (
                <p
                  key={`section-${idx}`}
                  className="text-[10px] font-semibold text-neutral-600 uppercase tracking-wider px-3 pt-3 pb-1"
                >
                  {entry.label}
                </p>
              )
            }
            const { to, label, icon: Icon, exact } = entry
            const fullPath = to.replace('$id', id)
            const active = exact
              ? location.pathname === fullPath
              : location.pathname === fullPath || location.pathname.startsWith(fullPath + '/')
            return (
              <Link
                key={to}
                to={to}
                params={{ id }}
                className={cn(
                  'flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition',
                  active
                    ? 'bg-neutral-800 text-white'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50',
                )}
              >
                <Icon className="size-4" />
                {label}
              </Link>
            )
          })}
        </nav>

        <div className="p-3 border-t border-neutral-800 space-y-2">
          <Link
            to="/tenants/$id"
            params={{ id }}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-purple-400 hover:bg-purple-950/30 transition"
          >
            <Shield className="size-4" />
            Acciones de plataforma
          </Link>
          {tenant && (
            <Badge variant={tenant.status === 'active' ? 'success' : 'warning'} className="w-full justify-center">
              {tenant.status}
            </Badge>
          )}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto flex flex-col">
        {/* Support mode banner — siempre visible cuando un superadmin está dentro de un tenant */}
        <div className="bg-purple-900/30 border-b border-purple-800 px-6 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Shield className="size-4 text-purple-400 shrink-0" />
            <span className="text-purple-200">
              <strong>Modo soporte</strong> — viendo <span className="font-semibold">{tenant?.name ?? '...'}</span> con fines de soporte. Tener cuidado con las modificaciones — impactan la tienda real.
            </span>
          </div>
          <Link
            to="/tenants/$id"
            params={{ id }}
            className="text-xs text-purple-300 hover:text-purple-100 underline shrink-0 whitespace-nowrap"
          >
            Salir
          </Link>
        </div>
        {tenant && tenant.metadata?.onboardingState && tenant.metadata.onboardingState !== 'completed' && location.pathname !== `/store/${id}/onboarding` && (
          <Link
            to="/store/$id/onboarding"
            params={{ id }}
            className="bg-blue-900/30 border-b border-blue-800 px-6 py-2.5 flex items-center justify-between gap-4 hover:bg-blue-900/40 transition"
          >
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="size-4 text-blue-400 shrink-0" />
              <span className="text-blue-200">
                <strong>Configuración pendiente</strong> — completá el onboarding de tu tienda para empezar a vender.
              </span>
            </div>
            <span className="text-xs text-blue-300 shrink-0">Continuar →</span>
          </Link>
        )}
        <div className="flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

/**
 * Header del sidebar — distingue modo superadmin (link "Volver al panel") vs
 * owner (dropdown de tenants accesibles + email + logout). Se compone arriba
 * en el `<aside>`.
 */
function SidebarHeader({ currentTenantId, tenant }: {
  currentTenantId: string
  tenant: Tenant | undefined
}) {
  const navigate = useNavigate()
  const { mode, ownerMemberships, userEmail, logout: clearAuth, setActiveTenant } = useAuth()
  const [open, setOpen] = useState(false)

  const isOwner = mode === 'owner'

  function switchTenant(tenantId: string) {
    setActiveTenant(tenantId, null)
    setOpen(false)
    void navigate({ to: '/store/$id', params: { id: tenantId } })
  }

  function handleLogout() {
    clearAuth()
    void navigate({ to: '/login' })
  }

  return (
    <div className="px-5 py-4 border-b border-neutral-800">
      {!isOwner ? (
        <Link to="/tenants" className="text-xs text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1">
          <ArrowLeft className="size-3" /> Volver al panel
        </Link>
      ) : (
        <p className="text-xs text-neutral-500">{userEmail}</p>
      )}

      <div className="mt-2 relative">
        <button
          type="button"
          onClick={() => isOwner && ownerMemberships.length > 1 && setOpen((v) => !v)}
          className={cn(
            'w-full text-left rounded-md',
            isOwner && ownerMemberships.length > 1 && 'hover:bg-neutral-800 px-2 -mx-2 py-1 -my-1 cursor-pointer',
          )}
          disabled={!isOwner || ownerMemberships.length <= 1}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="font-semibold tracking-tight truncate">{tenant?.name ?? 'Tienda'}</h1>
              <p className="text-xs text-neutral-500 mt-0.5 truncate">
                {tenant?.slug && <span className="font-mono">{tenant.slug}</span>}
                {tenant && <> · {tenant.defaultCurrency}</>}
              </p>
            </div>
            {isOwner && ownerMemberships.length > 1 && (
              <ChevronDown className={cn('size-4 text-neutral-500 transition-transform shrink-0', open && 'rotate-180')} />
            )}
          </div>
        </button>

        {open && (
          <div className="absolute left-0 right-0 top-full mt-2 z-30 rounded-md border border-neutral-700 bg-neutral-900 shadow-xl overflow-hidden">
            <p className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 px-3 pt-2 pb-1">Cambiar de tienda</p>
            {ownerMemberships.map((m) => (
              <button
                key={m.tenantId}
                onClick={() => switchTenant(m.tenantId)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-800 transition"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{m.tenantName}</p>
                  <p className="text-[10px] text-neutral-500 font-mono truncate">{m.tenantSlug} · {m.role}</p>
                </div>
                {m.tenantId === currentTenantId && <Check className="size-4 text-emerald-400" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {isOwner && (
        <button
          onClick={handleLogout}
          className="mt-3 flex items-center gap-2 text-xs text-neutral-500 hover:text-red-400 transition"
        >
          <LogOut className="size-3" /> Cerrar sesión
        </button>
      )}

    </div>
  )
}
